// CreateMemberBeMatched.jsx
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberBeMatched.module.scss";
import { BE_MATCHED } from "@/enums/lifestyleEnums";
import { useAuth } from "@/app/auth-context-state";

/* Welcome-style hero placeholders (same as Event Attract - q15) */
import mrImg from "@/assets/UI/memberCreate/q17Mr.webp";
import mrsImg from "@/assets/UI/memberCreate/q17Mrs.webp";
import mrMImg from "@/assets/UI/memberCreate/q17MrM.webp";
import mrsMImg from "@/assets/UI/memberCreate/q17MrsM.webp";

/* ---- Framer animations (same feel as EventAttract) ---- */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 1, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

/* Outlet context hook */
function useMemberLayoutContext() {
  return useOutletContext();
}

function toggleChoice(current, value) {
  if (current.includes(value)) return current.filter((v) => v !== value);
  return [...current, value];
}

/* Emoji meta for be_matched options */
const BE_MATCHED_META = {
  couple_to_couple: "💞",
  single_plus_couple: "👩‍❤️‍👨",
  group_trips: "🧑‍🤝‍🧑",
  mixed_circles: "🌈",
  exclusive_parties: "🍸",
  private_retreats: "🕯️",
};

/* Fallback English labels */
const BE_MATCHED_LABELS = {
  couple_to_couple: "Couple-to-couple travel & events",
  single_plus_couple: "Single + couple friendly meetups",
  group_trips: "Group trips & social getaways",
  mixed_circles: "Mixed open-minded circles for all lifestyle types",
  exclusive_parties: "Exclusive Noty invite-only parties and city events",
  private_retreats: "Private luxury weekends & destination retreats",
};

/* Closing text key helper – personalized by member’s gender */
function getClosingKey(rawGender, isCouple) {
  if (isCouple) return "onboarding.be_matched.closing.couple";
  if (rawGender === "man" || rawGender === "trans_man") {
    return "onboarding.be_matched.closing.man";
  }
  if (rawGender === "woman" || rawGender === "trans_woman") {
    return "onboarding.be_matched.closing.woman";
  }
  return "onboarding.be_matched.closing.other";
}

export default function CreateMemberBeMatched() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx, rawGender } = layoutCtx;
  const isCouple = Boolean(isCoupleCtx || memberType === "couple");

  const [submitting, setSubmitting] = useState(false);
  const [memberChoices, setMemberChoices] = useState([]); // string[]
  const [partnerChoices, setPartnerChoices] = useState([]); // string[]

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (k, o) => i18n.t(k, o);

  const title = t("onboarding.be_matched.title", {
    defaultValue: "How do you want to be matched",
  });

  // Intro lines (keeps your existing key structure but shows as simple lines)
  const introLines = useMemo(() => {
    if (isCouple) {
      return [
        t("onboarding.be_matched.couple.intro1", {
          defaultValue: "Tell us what kind of adventures you both want.",
        }),
        t("onboarding.be_matched.couple.intro2", {
          defaultValue: "Pick your favourites… then let your partner pick too.",
        }),
        t("onboarding.be_matched.couple.question1", {
          defaultValue: "What should we match you for first",
        }),
        t("onboarding.be_matched.couple.question2", {
          defaultValue: "Choose what feels exciting and realistic for you",
        }),
      ].filter(Boolean);
    }

    if (rawGender === "man" || rawGender === "trans_man") {
      return [
        t("onboarding.be_matched.single.man.intro1", { defaultValue: "" }),
        t("onboarding.be_matched.single.man.intro2", { defaultValue: "" }),
        t("onboarding.be_matched.single.man.intro3", { defaultValue: "" }),
        t("onboarding.be_matched.single.man.intro4", { defaultValue: "" }),
      ].filter(Boolean);
    }

    if (rawGender === "woman" || rawGender === "trans_woman") {
      return [
        t("onboarding.be_matched.single.woman.intro1", { defaultValue: "" }),
        t("onboarding.be_matched.single.woman.intro2", { defaultValue: "" }),
        t("onboarding.be_matched.single.woman.intro3", { defaultValue: "" }),
        t("onboarding.be_matched.single.woman.intro4", { defaultValue: "" }),
      ].filter(Boolean);
    }

    return [
      t("onboarding.be_matched.single.other.intro1", { defaultValue: "" }),
      t("onboarding.be_matched.single.other.intro2", { defaultValue: "" }),
      t("onboarding.be_matched.single.other.intro3", { defaultValue: "" }),
      t("onboarding.be_matched.single.other.intro4", { defaultValue: "" }),
    ].filter(Boolean);
  }, [isCouple, rawGender]);

  const memberBlockTitle = t("onboarding.be_matched.member.legend", {
    defaultValue: "Pick what you want to be matched for",
  });

  const partnerBlockTitle = t("onboarding.be_matched.partner.turn", {
    defaultValue: "Now it’s your partner’s turn…",
  });

  const guideTitle = t("onboarding.be_matched.guide.title", {
    defaultValue: "Quick note",
  });
  const guideBody = t("onboarding.be_matched.guide.body", {
    defaultValue:
      "This doesn’t lock you in. It just helps us suggest the right people and events.",
  });

  const baseOptions =
    Array.isArray(BE_MATCHED) && BE_MATCHED.length
      ? BE_MATCHED
      : Object.keys(BE_MATCHED_LABELS);

  const options = baseOptions.map((value) => ({
    value,
    emoji: BE_MATCHED_META[value] || "💫",
    title: t(`onboarding.be_matched.options.${value}.label`, {
      defaultValue: BE_MATCHED_LABELS[value] || value,
    }),
    hint: t(`onboarding.be_matched.tapToSelect.${value}`, {
      defaultValue: "Tap to add this to your event vibe",
    }),
  }));

  const hasMember = memberChoices.length > 0;
  const hasPartner = partnerChoices.length > 0;

  const closingKey =
    hasMember && (!isCouple || hasPartner)
      ? getClosingKey(rawGender, isCouple)
      : null;

  const closingText = closingKey ? t(closingKey, { defaultValue: "" }) : "";

  const submitDisabled =
    submitting || !memberChoices.length || (isCouple && !partnerChoices.length);

  async function onSubmit(e) {
    e.preventDefault();

    if (!memberChoices.length) {
      toast.info(
        t("onboarding.be_matched.toast.member_required", {
          defaultValue:
            "Choose at least one type of adventure you’d like to be matched for.",
        }),
      );
      return;
    }

    if (isCouple && !partnerChoices.length) {
      toast.info(
        t("onboarding.be_matched.toast.partner_required", {
          defaultValue: "Let your partner choose at least one too.",
        }),
      );
      return;
    }

    const payload = isCouple
      ? { memberBeMatched: memberChoices, partnerBeMatched: partnerChoices }
      : { memberBeMatched: memberChoices };

    setSubmitting(true);
    try {
      const { data } = await api.post("/v1/member-create/be_matched", payload, {
        skipToast: true,
      });

      await refreshMe();
      navigate(data?.next || "/member/join_host", { replace: true });
    } catch (err) {
      console.error("update be_matched failed:", err);
      toast.error(
        t("onboarding.be_matched.toast.save_error", {
          defaultValue: "We couldn’t save this. Please try again.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  function renderOptionsBlock({ name, blockTitle, choices, setChoices }) {
    return (
      <div className={cls.block}>
        {blockTitle ? <div className={cls.blockTitle}>{blockTitle}</div> : null}

        <fieldset className={cls.options}>
          <legend className="sr-only">{blockTitle || title}</legend>

          {options.map((opt) => {
            const checked = choices.includes(opt.value);

            return (
              <label
                key={`${name}-${opt.value}`}
                className={`${cls.option} ${checked ? cls.optionActive : ""}`}
              >
                <input
                  type="checkbox"
                  name={name}
                  value={opt.value}
                  checked={checked}
                  onChange={() =>
                    setChoices((prev) => toggleChoice(prev, opt.value))
                  }
                />

                <span className={cls.optionBody}>
                  <span className={cls.optionEmoji} aria-hidden="true">
                    {opt.emoji}
                  </span>

                  <span className={cls.optionText}>
                    <strong>{opt.title}</strong>
                    <small>{opt.hint}</small>
                  </span>

                  <span className={cls.optionDot} aria-hidden="true" />
                </span>
              </label>
            );
          })}
        </fieldset>
      </div>
    );
  }

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Single-page scroll content (no nested scroll) */}
      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>

              {introLines.length ? (
                introLines.map((line) => (
                  <div key={line} className={cls.formSub}>
                    {line}
                  </div>
                ))
              ) : (
                <>
                  <div className={cls.formSub}>
                    {t("onboarding.be_matched.intro1", {
                      defaultValue:
                        "Tell us what kind of nights and adventures you want to be invited into.",
                    })}
                  </div>
                  <div className={cls.formSub}>
                    {t("onboarding.be_matched.intro2", {
                      defaultValue:
                        "Choose a few. The more accurate you are, the better your matches feel.",
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          <form className={cls.form} onSubmit={onSubmit} noValidate>
            {renderOptionsBlock({
              name: "memberBeMatched",
              blockTitle: memberBlockTitle,
              choices: memberChoices,
              setChoices: setMemberChoices,
            })}

            {isCouple
              ? renderOptionsBlock({
                  name: "partnerBeMatched",
                  blockTitle: partnerBlockTitle,
                  choices: partnerChoices,
                  setChoices: setPartnerChoices,
                })
              : null}

            <div className={cls.readNote}>
              <strong>💡 {guideTitle}</strong>
              <p>{guideBody}</p>
            </div>

            {closingText ? (
              <div className={cls.closingNote} aria-live="polite">
                {closingText.split("\n").map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            ) : null}

            <div className={cls.actions}>
              <button
                type="submit"
                className={cls.submit}
                disabled={submitDisabled}
                aria-busy={submitting ? "true" : "false"}
              >
                {submitting
                  ? t("common.saving", { defaultValue: "Saving…" })
                  : t("common.continue", { defaultValue: "Continue" })}
              </button>
            </div>
          </form>
        </div>

        <div className={cls.bottomSpace} />
      </div>
    </main>
  );
}
